'use client';

import React, { useState, useEffect } from 'react';
import {
  Wind,
  Heart,
  Pill,
  FileText,
  Activity,
  TrendingUp,
  TrendingDown,
  Minus,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { createClient } from '@/lib/supabase/client';

interface Prescription {
  id: string;
  medicine_name: string;
  dose: string;
  frequency: string;
  medicine_type: string;
  is_active: boolean;
}

interface VisitRecord {
  id: string;
  visit_date: string;
  diagnosis: string;
  spo2: number | null;
  notes: string | null;
  prescriptions: string[];
}

interface Vital {
  spo2: number | null;
  heart_rate: number | null;
  blood_pressure: string | null;
  recorded_date: string | null;
}

interface SpO2Point {
  date: string;
  value: number;
}

const CustomTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: { value: number }[];
  label?: string;
}) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-md">
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-sm font-bold text-teal-600">{payload[0].value}% SpO₂</p>
      </div>
    );
  }
  return null;
};

export default function MyHealthPage() {
  const [expandedVisit, setExpandedVisit] = useState<number | null>(0);
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [visitHistory, setVisitHistory] = useState<VisitRecord[]>([]);
  const [latestVitals, setLatestVitals] = useState<Vital | null>(null);
  const [spO2History, setSpO2History] = useState<SpO2Point[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadHealthData = async () => {
      try {
        const patientPhone = localStorage.getItem('patientPhone') || '';
        if (!patientPhone) {
          setIsLoading(false);
          return;
        }

        const normalizedPhone = patientPhone.replace(/^\+91/, '').replace(/\D/g, '');
        const phoneVariants = [
          normalizedPhone,
          `+91${normalizedPhone}`,
          patientPhone,
        ];

        const supabase = createClient();
        if (!supabase) {
          setIsLoading(false);
          return;
        }

        // Fetch prescriptions
        const { data: rxData } = await supabase
          .from('prescriptions')
          .select('id, medicine_name, dose, frequency, medicine_type, is_active')
          .in('patient_phone', phoneVariants)
          .order('created_at', { ascending: false });

        if (rxData) setPrescriptions(rxData);

        // Fetch visit history with linked prescriptions
        const { data: visitsData } = await supabase
          .from('visit_history')
          .select('id, visit_date, diagnosis, spo2, notes')
          .in('patient_phone', phoneVariants)
          .order('created_at', { ascending: false });

        if (visitsData && visitsData.length > 0) {
          // Fetch visit prescriptions for each visit
          const visitIds = visitsData.map((v) => v.id);
          const { data: vpData } = await supabase
            .from('visit_prescriptions')
            .select('visit_id, prescription_text')
            .in('visit_id', visitIds);

          const vpMap: Record<string, string[]> = {};
          vpData?.forEach((vp) => {
            if (!vpMap[vp.visit_id]) vpMap[vp.visit_id] = [];
            vpMap[vp.visit_id].push(vp.prescription_text);
          });

          const enriched: VisitRecord[] = visitsData.map((v) => ({
            id: v.id,
            visit_date: v.visit_date,
            diagnosis: v.diagnosis,
            spo2: v.spo2,
            notes: v.notes,
            prescriptions: vpMap[v.id] || [],
          }));
          setVisitHistory(enriched);

          // Build SpO2 history chart from visits
          const spo2Points: SpO2Point[] = visitsData
            .filter((v) => v.spo2 !== null)
            .slice(0, 6)
            .reverse()
            .map((v) => ({
              date: v.visit_date?.slice(0, 6) || '',
              value: v.spo2 as number,
            }));
          setSpO2History(spo2Points);
        }

        // Fetch latest vitals
        const { data: vitalsData } = await supabase
          .from('vitals')
          .select('spo2, heart_rate, blood_pressure, recorded_date')
          .in('patient_phone', phoneVariants)
          .order('created_at', { ascending: false })
          .limit(1);

        if (vitalsData?.[0]) {
          setLatestVitals(vitalsData[0]);
        }
      } catch {
        // Non-critical — show empty state
      }
      setIsLoading(false);
    };

    loadHealthData();
  }, []);

  const currentSpO2 = latestVitals?.spo2 ?? visitHistory[0]?.spo2 ?? null;
  const prevSpO2 = visitHistory[1]?.spo2 ?? null;
  const trend =
    currentSpO2 !== null && prevSpO2 !== null
      ? currentSpO2 > prevSpO2
        ? 'up'
        : currentSpO2 < prevSpO2
        ? 'down'
        : 'stable' :'stable';

  if (isLoading) {
    return (
      <div className="max-w-lg mx-auto px-4 pt-6 space-y-4">
        <div className="h-8 bg-gray-100 rounded-xl animate-pulse w-40" />
        <div className="h-32 bg-gray-100 rounded-2xl animate-pulse" />
        <div className="h-40 bg-gray-100 rounded-2xl animate-pulse" />
        <div className="h-48 bg-gray-100 rounded-2xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-4 pt-6 space-y-6 pb-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-extrabold text-gray-900">My Health</h1>
        <p className="text-sm text-gray-500 mt-1">Your clinical summary &amp; records</p>
      </div>

      {/* Vitals Summary Card */}
      <div className="bg-teal-600 rounded-2xl p-5 text-white">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-xs font-semibold text-white/70 uppercase tracking-wide">Health Overview</p>
            <p className="text-xl font-extrabold mt-1">
              {visitHistory[0]?.diagnosis || 'No records yet'}
            </p>
            <p className="text-sm text-white/70 mt-0.5">
              {visitHistory.length > 0 ? 'Under Treatment' : 'Visit your doctor to add records'}
            </p>
          </div>
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <Wind size={22} className="text-white" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 pt-3 border-t border-white/20">
          <div className="text-center">
            <p className="text-lg font-extrabold tabular-nums">
              {currentSpO2 !== null ? `${currentSpO2}%` : '—'}
            </p>
            <p className="text-[10px] text-white/60 font-medium">SpO₂</p>
          </div>
          <div className="text-center border-x border-white/20">
            <p className="text-lg font-extrabold tabular-nums">{visitHistory.length}</p>
            <p className="text-[10px] text-white/60 font-medium">Visits</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-extrabold tabular-nums">
              {latestVitals?.heart_rate ? `${latestVitals.heart_rate}` : '—'}
            </p>
            <p className="text-[10px] text-white/60 font-medium">Heart Rate</p>
          </div>
        </div>
      </div>

      {/* SpO2 Trend Chart — only if data exists */}
      {spO2History.length > 1 && (
        <div className="bg-white border border-gray-100 rounded-2xl p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-bold text-gray-900">SpO₂ Trend</p>
              <p className="text-xs text-gray-400">From visit history</p>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-lg font-extrabold text-gray-900 tabular-nums">
                {currentSpO2 !== null ? `${currentSpO2}%` : '—'}
              </span>
              {trend === 'up' && <TrendingUp size={16} className="text-green-500" />}
              {trend === 'down' && <TrendingDown size={16} className="text-red-500" />}
              {trend === 'stable' && <Minus size={16} className="text-gray-400" />}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={120}>
            <LineChart data={spO2History} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <YAxis domain={[85, 100]} tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="value"
                stroke="#0d9488"
                strokeWidth={2.5}
                dot={{ fill: '#0d9488', r: 3 }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Latest Vitals Strip */}
      {latestVitals && (
        <div className="grid grid-cols-2 gap-3">
          {latestVitals.blood_pressure && (
            <div className="bg-white border border-gray-100 rounded-xl p-3.5">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">Blood Pressure</p>
              <div className="flex items-center gap-1.5">
                <Heart size={14} className="text-red-400" />
                <p className="text-sm font-semibold text-gray-900">{latestVitals.blood_pressure}</p>
              </div>
            </div>
          )}
          {latestVitals.heart_rate && (
            <div className="bg-white border border-gray-100 rounded-xl p-3.5">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">Heart Rate</p>
              <div className="flex items-center gap-1.5">
                <Activity size={14} className="text-teal-500" />
                <p className="text-sm font-semibold text-gray-900">{latestVitals.heart_rate} bpm</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Prescriptions */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Pill size={16} className="text-teal-600" />
          <h2 className="text-sm font-bold text-gray-900">Current Medications</h2>
        </div>
        {prescriptions.length === 0 ? (
          <div className="bg-white border border-gray-100 rounded-xl p-5 text-center">
            <Pill size={28} className="text-gray-200 mx-auto mb-2" />
            <p className="text-sm text-gray-400">No prescriptions on record</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {prescriptions.map((med) => (
              <div
                key={med.id}
                className={`bg-white border rounded-xl p-4 ${med.is_active ? 'border-gray-100' : 'border-gray-100 opacity-60'}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-bold text-gray-900">{med.medicine_name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {med.dose} · {med.frequency}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="text-[10px] bg-teal-50 text-teal-700 font-semibold px-2 py-0.5 rounded-full">
                      {med.medicine_type}
                    </span>
                    {med.is_active ? (
                      <span className="flex items-center gap-1 text-[10px] text-green-600 font-semibold">
                        <CheckCircle2 size={10} /> Active
                      </span>
                    ) : (
                      <span className="text-[10px] text-gray-400 font-semibold">Completed</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Visit History */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <FileText size={16} className="text-teal-600" />
          <h2 className="text-sm font-bold text-gray-900">Visit History</h2>
        </div>
        {visitHistory.length === 0 ? (
          <div className="bg-white border border-gray-100 rounded-xl p-5 text-center">
            <FileText size={28} className="text-gray-200 mx-auto mb-2" />
            <p className="text-sm text-gray-400">No visit records found</p>
            <p className="text-xs text-gray-300 mt-1">Your doctor will add records after each visit</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {visitHistory.map((visit, i) => (
              <div key={visit.id} className="bg-white border border-gray-100 rounded-xl overflow-hidden">
                <button
                  onClick={() => setExpandedVisit(expandedVisit === i ? null : i)}
                  className="w-full flex items-center justify-between px-4 py-3.5 text-left"
                >
                  <div>
                    <p className="text-sm font-bold text-gray-900">{visit.visit_date}</p>
                    <p className="text-xs text-gray-500 mt-0.5 truncate max-w-[220px]">{visit.diagnosis}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {visit.spo2 !== null && (
                      <div className="flex items-center gap-1">
                        <Activity
                          size={12}
                          className={visit.spo2 < 92 ? 'text-red-500' : 'text-green-500'}
                        />
                        <span
                          className={`text-xs font-bold tabular-nums ${
                            visit.spo2 < 92 ? 'text-red-500' : 'text-green-500'
                          }`}
                        >
                          {visit.spo2}%
                        </span>
                      </div>
                    )}
                    {expandedVisit === i ? (
                      <ChevronUp size={16} className="text-gray-400" />
                    ) : (
                      <ChevronDown size={16} className="text-gray-400" />
                    )}
                  </div>
                </button>
                {expandedVisit === i && (
                  <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-3">
                    {visit.notes && (
                      <p className="text-xs text-gray-500 leading-relaxed">{visit.notes}</p>
                    )}
                    {visit.prescriptions.length > 0 && (
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">
                          Prescriptions
                        </p>
                        <ul className="space-y-1">
                          {visit.prescriptions.map((rx, j) => (
                            <li key={j} className="flex items-start gap-2 text-xs text-gray-800">
                              <span className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-1.5 shrink-0" />
                              {rx}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
